import { combineReducers } from 'redux';
import { configureStore } from '@reduxjs/toolkit';
import { createEpicMiddleware, combineEpics } from 'redux-observable';
import { createLogger } from 'redux-logger';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import compressTransform from 'redux-persist-transform-compress';

// Slices
import userReducer from './slices/userSlice';
import teamsReducer from './slices/teamsSlice';
import assistantsReducer from './slices/assistantsSlice';
import fileReducer from './slices/fileSlice';

// Epics
import { loginEpic } from './epics/userEpic';
import {
  fetchTeamsEpic,
  teamSettingEpic,
  teamDeleteEpic,
  teamCreateEpic,
  fetchTeamMembersEpic,
  inviteTeamMemberEpic,
  updateTeamMemberRoleEpic,
  deleteTeamMemberEpic,
  cancelTeamInvitationEpic,
} from './epics/teamsEpic';
import {
  fetchAssistantsEpic,
  fetchAssistantDefaultEpic,
  fetchFileOptionsEpic,
  createAssistantEpic,
  deleteAssistantEpic,
  fetchUpdateFormEpic,
  updateAssistantEpic,
} from './epics/assistantsEpic';
import {
  fetchFilesEpic,
  uploadFilesEpic,
  deleteFileEpic,
} from './epics/fileEpic';

// Combine all epics
const rootEpic = combineEpics(
  loginEpic,
  fetchTeamsEpic,
  teamSettingEpic,
  teamDeleteEpic,
  teamCreateEpic,
  fetchTeamMembersEpic,
  inviteTeamMemberEpic,
  updateTeamMemberRoleEpic,
  deleteTeamMemberEpic,
  cancelTeamInvitationEpic,
  fetchAssistantsEpic,
  fetchFilesEpic,
  uploadFilesEpic,
  deleteFileEpic,
  fetchAssistantDefaultEpic,
  fetchFileOptionsEpic,
  createAssistantEpic,
  deleteAssistantEpic,
  fetchUpdateFormEpic,
  updateAssistantEpic,
);

// PersistConfig
const userPersistConfig = {
  key: 'ai_chat_new_django_user',
  storage,
  blacklist: ['error', 'loading'],
  transforms: [compressTransform()],
};
const teamsPersistConfig = {
  key: 'ai_chat_new_django_teams',
  storage,
  blacklist: ['error', 'loading'],
  transforms: [compressTransform()],
};
const assistantsPersistConfig = {
  key: 'ai_chat_new_django_assistants',
  storage,
  blacklist: ['error', 'loading'],
  transforms: [compressTransform()],
};
const filePersistConfig = {
  key: 'ai_chat_new_django_file',
  storage,
  blacklist: ['error', 'loading'],
  transforms: [compressTransform()],
};

const appReducer = combineReducers({
  user: persistReducer(userPersistConfig, userReducer),
  teams: persistReducer(teamsPersistConfig, teamsReducer),
  assistants: persistReducer(assistantsPersistConfig, assistantsReducer),
  file: persistReducer(filePersistConfig, fileReducer),
});

// Add reset logic to rootReducer
const rootReducer = (state, action) => {
  if (action.type === 'RESET_APP') {
    state = undefined; // All slices return to initial state
  }
  return appReducer(state, action);
};

// Persist config（根層排除 map，避免雙重持久化）
const persistConfig = {
  key: 'ai_chat_new_django',
  storage,
  stateReconciler: autoMergeLevel2,
  transforms: [compressTransform()],
  blacklist: ['user', 'teams', 'assistants', 'file'],
};

// Persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Epic middleware
const epicMiddleware = createEpicMiddleware();
const logger = createLogger({ collapsed: true });

// Create store
export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(epicMiddleware, logger),
  preloadedState: {},
});

// Create persistor
export const persistor = persistStore(store, null, () => {});

// Start epic middleware
epicMiddleware.run(rootEpic);

console.log('%c[store] 初始化 store', 'color: white; background: red; padding: 2px;');